import { useState, useEffect } from 'react';
import { 
  Settings, Save, RefreshCw, Database, Shield, Bell, Globe, CreditCard,
  Mail, Lock, Upload, Download, Clock, Zap, Eye, EyeOff, Server,
  Key, Users, Activity, BarChart, Palette, Image, AlertTriangle, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SystemSettings() {
  const [settings, setSettings] = useState({
    // Genel
    siteName: 'Ehliyet.nu',
    siteDescription: 'İsveç Sürücü Belgesi Teorik Sınavı',
    siteLanguage: 'tr',
    timezone: 'Europe/Stockholm',
    
    // Güvenlik
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: true,
    enable2FA: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    
    // Kullanıcı Yönetimi
    autoApproveUsers: true,
    defaultUserRole: 'student',
    freeQuestionsLimit: 10,
    
    // Sınav
    maxQuestionsPerExam: 70,
    passingScore: 52,
    examTimeLimit: 50,
    randomizeQuestions: true,
    randomizeChoices: true,
    showCorrectAnswers: true,
    allowExamRetry: true,
    
    // Bildirimler
    emailNotifications: true,
    smsNotifications: false,
    welcomeEmail: true,
    examResultEmail: true,
    subscriptionEmail: true,
    
    // Ödeme
    premiumPrice: 199,
    currency: 'SEK',
    stripeEnabled: true,
    swishEnabled: false,
    klarnaEnabled: false,
    
    // Tema & Görünüm
    primaryColor: '#dc2626',
    secondaryColor: '#f59e0b',
    darkMode: true,
    showFooter: true,
    
    // API & Performans
    apiRateLimit: 100,
    cacheEnabled: true,
    cacheDuration: 3600,
    
    // Yedekleme
    autoBackup: true,
    backupFrequency: 'daily',
    backupRetention: 30,
    
    // Raporlama
    weeklyReport: true,
    monthlyReport: true,
    dataRetentionDays: 365
  });

  const [saving, setSaving] = useState(false);
  
  // Real-time stats from API
  const [stats, setStats] = useState({
    total_questions: 0,
    total_users: 0,
    total_categories: 0,
    total_exams_completed: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch real stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const response = await axios.get(`${API}/stats?nocache=${Date.now()}`);
        setStats(response.data);
      } catch (error) {
        console.error('Stats fetch error:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setTimeout(() => {
      alert('✅ Ayarlar kaydedildi!');
      setSaving(false);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Header with Gradient */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 rounded-3xl blur opacity-50 group-hover:opacity-75 transition"></div>
        <div className="relative bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-black text-white flex items-center gap-3">
                <Settings className="w-10 h-10 animate-pulse" />
                ⚙️ Sistem Ayarları
              </h2>
              <p className="text-white/80 mt-2 text-lg">Tüm platform ayarlarını buradan kontrol edin</p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-8 py-4 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 hover:from-green-600 hover:via-emerald-600 hover:to-teal-700 text-white rounded-xl font-black text-lg transition-all flex items-center gap-3 disabled:opacity-50 shadow-2xl hover:scale-105"
            >
              {saving ? (
                <><RefreshCw className="w-6 h-6 animate-spin" /> Kaydediliyor...</>
              ) : (
                <><Save className="w-6 h-6" /> 💾 Tümünü Kaydet</>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 🌍 Genel Ayarlar */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Globe className="w-7 h-7 text-blue-400" />
              🌍 Genel Ayarlar
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">📝 Site Adı</label>
                <input
                  type="text"
                  value={settings.siteName}
                  onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="Ehliyet.nu"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">📄 Site Açıklaması</label>
                <textarea
                  value={settings.siteDescription}
                  onChange={(e) => setSettings({...settings, siteDescription: e.target.value})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2">🌐 Dil</label>
                  <select
                    value={settings.siteLanguage}
                    onChange={(e) => setSettings({...settings, siteLanguage: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="tr">🇹🇷 Türkçe</option>
                    <option value="sv">🇸🇪 Svenska</option>
                    <option value="en">🇬🇧 English</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2">🕐 Zaman Dilimi</label>
                  <select
                    value={settings.timezone}
                    onChange={(e) => setSettings({...settings, timezone: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Europe/Stockholm">Stockholm</option>
                    <option value="Europe/Istanbul">Istanbul</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🛡️ Güvenlik & Erişim */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-green-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Shield className="w-7 h-7 text-green-400" />
              🛡️ Güvenlik & Erişim
            </h3>
            <div className="space-y-4">
              <ToggleSetting 
                label="🔧 Bakım Modu"
                description="Siteyi geçici olarak kapat"
                value={settings.maintenanceMode}
                onChange={() => setSettings({...settings, maintenanceMode: !settings.maintenanceMode})}
              />
              <ToggleSetting 
                label="👥 Yeni Kayıtlar"
                description="Kullanıcı kaydına izin ver"
                value={settings.allowRegistration}
                onChange={() => setSettings({...settings, allowRegistration: !settings.allowRegistration})}
              />
              <ToggleSetting 
                label="✉️ Email Doğrulama"
                description="Kayıt sonrası email onayı iste"
                value={settings.requireEmailVerification}
                onChange={() => setSettings({...settings, requireEmailVerification: !settings.requireEmailVerification})}
              />
              <ToggleSetting 
                label="🔐 2FA (İki Faktörlü Doğrulama)"
                description="Ekstra güvenlik katmanı"
                value={settings.enable2FA}
                onChange={() => setSettings({...settings, enable2FA: !settings.enable2FA})}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2">⏱️ Oturum Süresi (dk)</label>
                  <input
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => setSettings({...settings, sessionTimeout: Number(e.target.value)})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2">🚫 Max Giriş Denemesi</label>
                  <input
                    type="number"
                    value={settings.maxLoginAttempts}
                    onChange={(e) => setSettings({...settings, maxLoginAttempts: Number(e.target.value)})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 👥 Kullanıcı Yönetimi */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Users className="w-7 h-7 text-purple-400" />
              👥 Kullanıcı Yönetimi
            </h3>
            <div className="space-y-4">
              <ToggleSetting 
                label="✅ Otomatik Onay"
                description="Yeni kullanıcıları otomatik aktifleştir"
                value={settings.autoApproveUsers}
                onChange={() => setSettings({...settings, autoApproveUsers: !settings.autoApproveUsers})}
              />
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">🎭 Varsayılan Rol</label>
                <select
                  value={settings.defaultUserRole}
                  onChange={(e) => setSettings({...settings, defaultUserRole: e.target.value})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="student">Öğrenci</option>
                  <option value="premium">Premium</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">🆓 Ücretsiz Soru Limiti</label>
                <input
                  type="number"
                  value={settings.freeQuestionsLimit}
                  onChange={(e) => setSettings({...settings, freeQuestionsLimit: Number(e.target.value)})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 📝 Sınav Ayarları */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-yellow-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Database className="w-7 h-7 text-yellow-400" />
              📝 Sınav Ayarları
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2">📊 Soru Sayısı</label>
                  <input
                    type="number"
                    value={settings.maxQuestionsPerExam}
                    onChange={(e) => setSettings({...settings, maxQuestionsPerExam: Number(e.target.value)})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2">✅ Geçme Puanı</label>
                  <input
                    type="number"
                    value={settings.passingScore}
                    onChange={(e) => setSettings({...settings, passingScore: Number(e.target.value)})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">⏰ Sınav Süresi (dakika)</label>
                <input
                  type="number"
                  value={settings.examTimeLimit}
                  onChange={(e) => setSettings({...settings, examTimeLimit: Number(e.target.value)})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <ToggleSetting 
                label="🔀 Soruları Karıştır"
                description="Her sınavda farklı sıra"
                value={settings.randomizeQuestions}
                onChange={() => setSettings({...settings, randomizeQuestions: !settings.randomizeQuestions})}
              />
              <ToggleSetting 
                label="🎲 Şıkları Karıştır"
                description="Cevap şıklarını rastgele sırala"
                value={settings.randomizeChoices}
                onChange={() => setSettings({...settings, randomizeChoices: !settings.randomizeChoices})}
              />
              <ToggleSetting 
                label="👁️ Doğru Cevapları Göster"
                description="Sınav sonrası doğru cevapları göster"
                value={settings.showCorrectAnswers}
                onChange={() => setSettings({...settings, showCorrectAnswers: !settings.showCorrectAnswers})}
              />
              <ToggleSetting 
                label="🔄 Sınav Tekrarı"
                description="Başarısız sınav tekrarına izin ver"
                value={settings.allowExamRetry}
                onChange={() => setSettings({...settings, allowExamRetry: !settings.allowExamRetry})}
              />
            </div>
          </div>
        </div>

        {/* 📧 Bildirimler */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Mail className="w-7 h-7 text-cyan-400" />
              📧 Bildirimler
            </h3>
            <div className="space-y-4">
              <ToggleSetting 
                label="📬 Email Bildirimleri"
                description="Kullanıcılara email gönder"
                value={settings.emailNotifications}
                onChange={() => setSettings({...settings, emailNotifications: !settings.emailNotifications})}
              />
              <ToggleSetting 
                label="📱 SMS Bildirimleri"
                description="SMS ile bilgilendirme"
                value={settings.smsNotifications}
                onChange={() => setSettings({...settings, smsNotifications: !settings.smsNotifications})}
              />
              <ToggleSetting 
                label="👋 Hoş Geldin Emaili"
                description="Yeni kayıt sonrası gönder"
                value={settings.welcomeEmail}
                onChange={() => setSettings({...settings, welcomeEmail: !settings.welcomeEmail})}
              />
              <ToggleSetting 
                label="📊 Sınav Sonuç Emaili"
                description="Sınav bitiminde gönder"
                value={settings.examResultEmail}
                onChange={() => setSettings({...settings, examResultEmail: !settings.examResultEmail})}
              />
              <ToggleSetting 
                label="💳 Abonelik Emaili"
                description="Ödeme ve abonelik bildirimleri"
                value={settings.subscriptionEmail}
                onChange={() => setSettings({...settings, subscriptionEmail: !settings.subscriptionEmail})}
              />
            </div>
          </div>
        </div>

        {/* 💳 Ödeme Sistemleri */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-pink-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <CreditCard className="w-7 h-7 text-pink-400" />
              💳 Ödeme Sistemleri
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">💰 Premium Fiyat</label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={settings.premiumPrice}
                    onChange={(e) => setSettings({...settings, premiumPrice: Number(e.target.value)})}
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                  <select
                    value={settings.currency}
                    onChange={(e) => setSettings({...settings, currency: e.target.value})}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="SEK">🇸🇪 SEK</option>
                    <option value="EUR">🇪🇺 EUR</option>
                    <option value="USD">🇺🇸 USD</option>
                  </select>
                </div>
              </div>
              <ToggleSetting 
                label="💳 Stripe"
                description="Kredi kartı ödemeleri"
                value={settings.stripeEnabled}
                onChange={() => setSettings({...settings, stripeEnabled: !settings.stripeEnabled})}
              />
              <ToggleSetting 
                label="📱 Swish"
                description="İsveç mobil ödeme"
                value={settings.swishEnabled}
                onChange={() => setSettings({...settings, swishEnabled: !settings.swishEnabled})}
              />
              <ToggleSetting 
                label="🛒 Klarna"
                description="Taksitli ödeme"
                value={settings.klarnaEnabled}
                onChange={() => setSettings({...settings, klarnaEnabled: !settings.klarnaEnabled})}
              />
            </div>
          </div>
        </div>

        {/* 🎨 Tema & Görünüm */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-indigo-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Palette className="w-7 h-7 text-indigo-400" />
              🎨 Tema & Görünüm
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">🎨 Ana Renk</label>
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({...settings, primaryColor: e.target.value})}
                  className="w-full h-12 rounded-lg cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">🌈 İkincil Renk</label>
                <input
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => setSettings({...settings, secondaryColor: e.target.value})}
                  className="w-full h-12 rounded-lg cursor-pointer"
                />
              </div>
              <ToggleSetting 
                label="🌙 Karanlık Mod"
                description="Koyu tema kullan"
                value={settings.darkMode}
                onChange={() => setSettings({...settings, darkMode: !settings.darkMode})}
              />
              <ToggleSetting 
                label="📄 Footer Göster"
                description="Alt bilgi bölümünü göster"
                value={settings.showFooter}
                onChange={() => setSettings({...settings, showFooter: !settings.showFooter})}
              />
            </div>
          </div>
        </div>

        {/* ⚡ API & Performans */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-orange-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Zap className="w-7 h-7 text-orange-400" />
              ⚡ API & Performans
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">🚦 API Rate Limit (istek/dk)</label>
                <input
                  type="number"
                  value={settings.apiRateLimit}
                  onChange={(e) => setSettings({...settings, apiRateLimit: Number(e.target.value)})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <ToggleSetting 
                label="💾 Cache Aktif"
                description="Hızlı erişim için cache kullan"
                value={settings.cacheEnabled}
                onChange={() => setSettings({...settings, cacheEnabled: !settings.cacheEnabled})}
              />
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">⏱️ Cache Süresi (saniye)</label>
                <input
                  type="number"
                  value={settings.cacheDuration}
                  onChange={(e) => setSettings({...settings, cacheDuration: Number(e.target.value)})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 💾 Yedekleme & Güvenlik */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-600 to-green-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-teal-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <Server className="w-7 h-7 text-teal-400" />
              💾 Yedekleme & Güvenlik
            </h3>
            <div className="space-y-4">
              <ToggleSetting 
                label="🔄 Otomatik Yedekleme"
                description="Verileri düzenli yedekle"
                value={settings.autoBackup}
                onChange={() => setSettings({...settings, autoBackup: !settings.autoBackup})}
              />
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">📅 Yedekleme Sıklığı</label>
                <select
                  value={settings.backupFrequency}
                  onChange={(e) => setSettings({...settings, backupFrequency: e.target.value})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="hourly">Her Saat</option>
                  <option value="daily">Günlük</option>
                  <option value="weekly">Haftalık</option>
                  <option value="monthly">Aylık</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">📦 Yedek Saklama (gün)</label>
                <input
                  type="number"
                  value={settings.backupRetention}
                  onChange={(e) => setSettings({...settings, backupRetention: Number(e.target.value)})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 📊 Raporlama */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl blur opacity-30 group-hover:opacity-60 transition"></div>
          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-violet-500/20 rounded-2xl p-6">
            <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
              <BarChart className="w-7 h-7 text-violet-400" />
              📊 Raporlama & Analitik
            </h3>
            <div className="space-y-4">
              <ToggleSetting 
                label="📅 Haftalık Rapor"
                description="Her hafta otomatik rapor"
                value={settings.weeklyReport}
                onChange={() => setSettings({...settings, weeklyReport: !settings.weeklyReport})}
              />
              <ToggleSetting 
                label="📆 Aylık Rapor"
                description="Her ay otomatik rapor"
                value={settings.monthlyReport}
                onChange={() => setSettings({...settings, monthlyReport: !settings.monthlyReport})}
              />
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">🗄️ Veri Saklama (gün)</label>
                <input
                  type="number"
                  value={settings.dataRetentionDays}
                  onChange={(e) => setSettings({...settings, dataRetentionDays: Number(e.target.value)})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 📊 Hızlı İstatistikler - Animated Cards */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
        <div className="relative bg-gradient-to-br from-blue-900/40 to-purple-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <h3 className="text-3xl font-black text-white mb-6 flex items-center gap-3">
            <Activity className="w-8 h-8 animate-pulse" />
            📊 Sistem İstatistikleri (Canlı)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <StatCard 
              value={statsLoading ? "..." : stats.total_questions.toLocaleString('tr-TR')} 
              label="Toplam Soru" 
              icon="📚" 
              color="blue" 
            />
            <StatCard 
              value={statsLoading ? "..." : stats.total_users.toLocaleString('tr-TR')} 
              label="Kayıtlı Kullanıcı" 
              icon="👥" 
              color="green" 
            />
            <StatCard 
              value={statsLoading ? "..." : stats.total_categories.toLocaleString('tr-TR')} 
              label="Kategori" 
              icon="📂" 
              color="purple" 
            />
            <StatCard 
              value={statsLoading ? "..." : stats.total_exams_completed.toLocaleString('tr-TR')} 
              label="Tamamlanan Sınav" 
              icon="🏆" 
              color="yellow" 
            />
          </div>
        </div>
      </div>

      {/* Hızlı Aksiyonlar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button className="relative group overflow-hidden bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white p-6 rounded-xl transition-all hover:scale-105 shadow-xl">
          <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="relative flex items-center gap-3">
            <Download className="w-6 h-6" />
            <div className="text-left">
              <div className="font-bold">💾 Yedek Al</div>
              <div className="text-xs opacity-80">Tüm verileri yedekle</div>
            </div>
          </div>
        </button>
        
        <button className="relative group overflow-hidden bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white p-6 rounded-xl transition-all hover:scale-105 shadow-xl">
          <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="relative flex items-center gap-3">
            <Upload className="w-6 h-6" />
            <div className="text-left">
              <div className="font-bold">📤 Yedek Yükle</div>
              <div className="text-xs opacity-80">Yedeği geri yükle</div>
            </div>
          </div>
        </button>
        
        <button className="relative group overflow-hidden bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white p-6 rounded-xl transition-all hover:scale-105 shadow-xl">
          <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="relative flex items-center gap-3">
            <AlertTriangle className="w-6 h-6" />
            <div className="text-left">
              <div className="font-bold">🔄 Sıfırla</div>
              <div className="text-xs opacity-80">Varsayılana dön</div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

// Toggle Switch Component
function ToggleSetting({ label, description, value, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all">
      <div>
        <p className="text-white font-bold">{label}</p>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
      <button
        onClick={onChange}
        className={`relative w-16 h-8 rounded-full transition-all shadow-lg ${
          value ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gray-700'
        }`}
      >
        <div className={`absolute w-6 h-6 bg-white rounded-full top-1 transition-transform shadow-md ${
          value ? 'translate-x-9' : 'translate-x-1'
        }`}>
          {value && <CheckCircle className="w-4 h-4 text-green-500 m-auto mt-1" />}
        </div>
      </button>
    </div>
  );
}

// Stat Card Component
function StatCard({ value, label, icon, color }) {
  const colors = {
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500',
    yellow: 'from-yellow-500 to-orange-500'
  };

  return (
    <div className="relative group">
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${colors[color]} rounded-xl blur opacity-30 group-hover:opacity-60 transition`}></div>
      <div className="relative bg-slate-900/80 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:scale-105 transition-all">
        <div className="text-4xl mb-2">{icon}</div>
        <div className="text-4xl font-black text-white mb-2">{value}</div>
        <div className="text-sm text-gray-300 font-semibold">{label}</div>
      </div>
    </div>
  );
}